/**
 * What: Serializable skeleton for Shopify resource-index tables (columns, views,
 * filters, sort, search). Behaviour (row data, badge tones beyond static maps,
 * primary-cell links) stays in caller-supplied functions — JSON holds structure.
 * Transport / data-flow: Parsed once per table definition; consumed by
 * resource-index/* components and ShopifyResourceIndex.
 */
import * as z from 'zod'

export const indexColumnKindSchema = z.enum(['primary', 'text', 'numeric', 'date', 'badge', 'image', 'custom'])
export type IndexColumnKind = z.infer<typeof indexColumnKindSchema>

export const indexBadgeToneSchema = z.enum(['success', 'neutral', 'info', 'warning', 'critical'])
export type IndexBadgeTone = z.infer<typeof indexBadgeToneSchema>

export const indexColumnSchema = z.object({
  id: z.string().min(1),
  /** Empty heading = headingless accessory column (Shopify Products thumbnail pattern). */
  heading: z.string(),
  kind: indexColumnKindSchema,
  listSlot: z.enum(['primary', 'secondary', 'inline', 'kicker', 'labeled']).optional(),
  showInList: z.boolean().optional(),
  /** Static value -> tone map for `badge` columns. Dynamic tones stay caller-side. */
  toneMap: z.record(z.string(), indexBadgeToneSchema).optional(),
})
export type IndexColumn = z.infer<typeof indexColumnSchema>

export const indexViewSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
})
export type IndexView = z.infer<typeof indexViewSchema>

export const indexFilterOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
})
export type IndexFilterOption = z.infer<typeof indexFilterOptionSchema>

export const indexFilterSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  options: z.array(indexFilterOptionSchema).optional(),
  multiple: z.boolean().optional(),
})
export type IndexFilter = z.infer<typeof indexFilterSchema>

export const indexSortOptionSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
})
export type IndexSortOption = z.infer<typeof indexSortOptionSchema>

export const resourceIndexSchemaSchema = z.object({
  id: z.string().min(1),
  resource: z.object({ singular: z.string().min(1), plural: z.string().min(1) }).optional(),
  columns: z.array(indexColumnSchema).min(1),
  views: z.array(indexViewSchema).min(1).optional(),
  filters: z.array(indexFilterSchema).optional(),
  sortOptions: z.array(indexSortOptionSchema).optional(),
  searchLabel: z.string().min(1).optional(),
  metadata: z
    .object({
      selection: z.literal('none').optional(),
      pagination: z.literal('client').optional(),
    })
    .optional(),
})
export type ResourceIndexSchema = z.infer<typeof resourceIndexSchemaSchema>

/** Throwing parse — matches repo's no-silent-defaults invariant. */
export function parseResourceIndexSchema(input: unknown): ResourceIndexSchema {
  return resourceIndexSchemaSchema.parse(input)
}
